// Combat HUD (bottom-left): the "J — COMBAT" toggle. Before entering combat a "Combat Mode" hint
// sits next to it; once in combat the hint is hidden and the J button glows red. The Q/ATK and
// E/BLOCK on-screen buttons are kept out of the way (keyboard Q/E still work).

import { weaponStore } from "../items/weaponStore.js";
import { injectTheme } from "./theme.js";

const STYLE_ID = "ghostland-combat-hud-style";
const CSS = `
/* Bottom-left, above the vitals (hunger/thirst) bars. */
#gl-hud-combatbar { position: fixed; left: 16px; bottom: 124px; z-index: 25; display: none; gap: 8px; align-items: center; }
#gl-hud-combatbar.show { display: flex; }
/* "Combat Mode" hint shown next to the J button until you enter combat. */
#gl-hud-combat-hint {
  display: flex; align-items: center; white-space: nowrap; padding: 0 4px;
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; font-size: 13px;
  letter-spacing: 1px; color: var(--gl-muted);
}
#gl-hud-combatbar.combat #gl-hud-combat-hint { display: none; }
.gl-hud-cbtn {
  position: relative; width: 52px; height: 52px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 1px; overflow: hidden;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 10px;
  color: var(--gl-text); font-family: ui-monospace, "SFMono-Regular", Menlo, monospace;
  cursor: pointer; user-select: none; box-shadow: 0 8px 24px rgba(0,0,0,.6);
  transition: border-color .1s, background .1s, box-shadow .1s;
}
.gl-hud-cbtn:hover { border-color: var(--gl-border-focus); background: var(--gl-panel-2); }
.gl-hud-cbtn .k { font-size: 18px; font-weight: 700; letter-spacing: 1px; }
.gl-hud-cbtn .l { font-size: 7px; letter-spacing: 1px; color: var(--gl-muted); }
#gl-hud-combat.active { border-color: var(--gl-error); box-shadow: 0 0 14px rgba(255,70,70,.55); }
#gl-hud-combat.active .l, #gl-hud-combat.active .k { color: var(--gl-error); }
/* the Q/ATK + E/BLOCK on-screen buttons stay hidden (keyboard Q/E still work). */
#gl-hud-atk, #gl-hud-block { display: none; }
.gl-hud-cd { position: absolute; inset: 0; background: rgba(0,0,0,.6); transform-origin: bottom;
  transform: scaleY(0); pointer-events: none; }
.gl-hud-cbtn.cooling .gl-hud-cd { transform: scaleY(1); transition: transform linear; }
`;

export interface CombatActions { toggle: () => void; attack: () => void; block: () => void; }

let bar: HTMLDivElement | null = null;
let combatBtn: HTMLDivElement | null = null;
let blockBtn: HTMLDivElement | null = null;
let atkBtn: HTMLDivElement | null = null;
let mounted = false;

function makeBtn(id: string, key: string, label: string, onClick: () => void): HTMLDivElement {
  const b = document.createElement("div");
  b.id = id; b.className = "gl-hud-cbtn";
  b.innerHTML = `<span class="k">${key}</span><span class="l">${label}</span><div class="gl-hud-cd"></div>`;
  b.addEventListener("click", onClick);
  return b;
}

/** Mount the combat HUD (idempotent). */
export function mountCombatHud(actions: CombatActions): void {
  if (mounted) return;
  mounted = true;
  injectTheme();
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style"); style.id = STYLE_ID; style.textContent = CSS; document.head.appendChild(style);
  }
  bar = document.createElement("div");
  bar.id = "gl-hud-combatbar";
  combatBtn = makeBtn("gl-hud-combat", "J", "COMBAT", actions.toggle);
  combatBtn.title = "Combat mode (J)";
  const hint = document.createElement("span");
  hint.id = "gl-hud-combat-hint";
  hint.textContent = "Combat Mode";
  atkBtn = makeBtn("gl-hud-atk", "Q", "ATTACK", actions.attack);
  blockBtn = makeBtn("gl-hud-block", "E", "BLOCK", actions.block);
  bar.append(combatBtn, hint, atkBtn, blockBtn);
  document.body.appendChild(bar);
  weaponStore.subscribe(() => bar!.classList.toggle("show", weaponStore.hasWeapon()));
}

/** Reflect whether combat mode is currently active (shows action buttons + red highlight). */
export function setCombatActive(on: boolean): void {
  combatBtn?.classList.toggle("active", on);
  bar?.classList.toggle("combat", on);
}

/** Start a cooldown sweep on the attack or block button. */
export function flashCooldown(which: "attack" | "block", ms: number): void {
  const b = which === "attack" ? atkBtn : blockBtn;
  if (!b) return;
  const cd = b.querySelector(".gl-hud-cd") as HTMLElement;
  b.classList.remove("cooling"); cd.style.transition = "none"; cd.style.transform = "scaleY(1)";
  void b.offsetWidth; // reflow so the transition restarts
  b.classList.add("cooling"); cd.style.transition = `transform ${ms}ms linear`; cd.style.transform = "scaleY(0)";
  window.setTimeout(() => b.classList.remove("cooling"), ms);
}
